// One-shot guard for the "stale chunk after a new deploy → reload once"
// recovery, shared by main.jsx's `vite:preloadError` handler and
// ErrorBoundary's componentDidCatch so the two can't drift (they previously
// duplicated the raw 'pg_chunk_reload_attempted' string in each file).
//
// EVERY sessionStorage access is guarded. Storage-disabled / sandboxed / some
// private-mode contexts throw a SecurityError on ANY access, and an unguarded
// throw inside componentDidCatch would crash the top-level error boundary into
// a white screen instead of recovering or showing the fallback UI.
//
// When sessionStorage is unavailable we fall back to window.name, which
// uniquely survives a same-tab reload — so the one-reload guard still holds and
// a genuinely-broken deploy can't spin the tab in an infinite reload loop.
// window.name is appended to (never overwritten) so we don't clobber anything
// else parked there.

const FLAG = 'pg_chunk_reload_attempted'
const NAME_TOKEN = '__pg_chunk_reloaded__'

/** True once a chunk-reload has been attempted this session/tab. */
export function hasAttemptedChunkReload() {
  try {
    if (sessionStorage.getItem(FLAG)) return true
  } catch {
    // sessionStorage unavailable — fall through to the window.name fallback.
  }
  try {
    return typeof window !== 'undefined' && window.name.includes(NAME_TOKEN)
  } catch {
    return false
  }
}

/** Record that we're about to reload, so the next failure won't loop. */
export function markChunkReloadAttempted() {
  try {
    sessionStorage.setItem(FLAG, String(Date.now()))
    return
  } catch {
    // sessionStorage unavailable — persist the guard on window.name instead,
    // which survives the reload we're about to trigger.
  }
  try {
    if (typeof window !== 'undefined' && !window.name.includes(NAME_TOKEN)) {
      window.name += NAME_TOKEN
    }
  } catch {
    // Nothing else to persist to; worst case is one extra reload, which still
    // lands on the working new build.
  }
}

/** Reset the guard — used by the fallback UI's manual "reload" / "home". */
export function clearChunkReloadFlag() {
  try { sessionStorage.removeItem(FLAG) } catch { /* storage off */ }
  try {
    if (typeof window !== 'undefined') window.name = window.name.replace(NAME_TOKEN, '')
  } catch { /* noop */ }
}
